/* React */
import React, { useState } from "react";

/* Utils */
import { isSegment } from "src/utils/guards/customCohorts";

/* Components */
import InitialSegment from "src/components/insights/custom-cohorts/cohort-builder/cohort-segment/initial-segment/InitialSegment";
import SegmentToolbar from "src/components/insights/custom-cohorts/cohort-builder/cohort-segment/segment-toolbar/SegmentToolbar";
import EntitySelector from "src/components/insights/custom-cohorts/cohort-builder/entity-selector/EntitySelector";
import DateRestriction from "src/components/insights/custom-cohorts/cohort-builder/date-restriction/DateRestriction";
import ValueSelector from "src/components/insights/custom-cohorts/cohort-builder/value-selector/ValueSelector";
import OperatorContainer from "src/components/insights/custom-cohorts/cohort-builder/cohort-segment/operator-container/OperatorContainer";

/* Stylesheet */
import styles from "./CohortSegment.module.scss";

/* Interfaces */
import {
  BasicProps,
  LogicalProps,
} from "src/shared/interfaces/custom-cohorts/CustomCohorts";

import { Operator } from "src/shared/interfaces/custom-cohorts/Operator";

interface CohortSegmentProps {
  segments: BasicProps | LogicalProps;
  featureTypes: BasicProps[];
  disabled: boolean;
  handleSegments: (
    segment: BasicProps | LogicalProps
  ) => void;
  handleDeleteSegment: (indexPath: number[]) => void;
}

const CohortSegment: React.FC<CohortSegmentProps> = ({
  segments,
  featureTypes,
  disabled,
  handleSegments,
  handleDeleteSegment,
}) => {
  const [showContent, setShowContent] =
    useState<boolean>(true);

  const handleUpdate = <K extends keyof BasicProps>(
    key: K,
    data?: {
      value?: BasicProps[K];
      values?: BasicProps[K];
      boundaries?: BasicProps[K];
    }
  ) => {
    handleSegments({
      ...segments,
      [key]:
        data?.value ?? data?.values ?? data?.boundaries,
    });
  };

  return !segments ? (
    <InitialSegment
      isSegment={isSegment}
      segments={segments}
      disabled={disabled}
      handleSegments={handleSegments}
    />
  ) : (
    <div
      style={{
        borderRadius: showContent ? "8px" : "48px",
      }}
      className={styles.cohort_segment}
    >
      <SegmentToolbar
        indexPath={[]}
        segments={segments}
        label={
          isSegment(segments)
            ? segments.operator
            : segments
            ? segments.entity.name
            : undefined
        }
        disabled={disabled}
        showContent={showContent}
        setShowContent={setShowContent}
        handleDeleteSegment={handleDeleteSegment}
      />
      {showContent ? (
        <div
          style={{
            borderRight: isSegment(segments)
              ? segments.operator === Operator.AND
                ? "5px solid var(--ion-color-primary)"
                : "5px solid var(--ion-color-secondary)"
              : undefined,
          }}
          className={styles.wrapper_container}
        >
          {segments && !isSegment(segments) ? (
            <>
              <EntitySelector
                value={segments.entity.name}
                featureTypes={featureTypes}
                disabled={disabled}
                handleSegments={handleSegments}
              />
              <DateRestriction
                dateRestriction={segments.dateRestriction}
                disabled={disabled}
                handleUpdate={handleUpdate}
              />
              <ValueSelector
                segment={segments}
                disabled={disabled}
                handleUpdate={handleUpdate}
              />
            </>
          ) : undefined}
          {isSegment(segments) &&
            segments?.conditions?.map(
              (
                condition: BasicProps | LogicalProps,
                index: number
              ) => (
                <div key={index}>
                  <CohortSegment
                    segments={condition}
                    featureTypes={featureTypes}
                    disabled={disabled}
                    handleSegments={(updatedCondition) => {
                      const updatedConditions = [
                        ...segments.conditions!,
                      ];
                      updatedConditions[index] =
                        updatedCondition;
                      handleSegments({
                        ...segments,
                        conditions: updatedConditions,
                      });
                    }}
                    handleDeleteSegment={(indexPath) =>
                      handleDeleteSegment([
                        index,
                        ...indexPath,
                      ])
                    }
                  />
                </div>
              )
            )}
          {isSegment(segments) || !segments ? (
            <OperatorContainer
              isSegment={isSegment}
              segments={segments}
              disabled={disabled}
              handleSegments={handleSegments}
            />
          ) : undefined}
        </div>
      ) : undefined}
    </div>
  );
};

export default CohortSegment;
